import { Injectable, OnDestroy } from '@angular/core';
import {
    CmpsDialog,
    CmpsDialogRef,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, merge, Observable, Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AuthState, AuthStatus } from '../../../auth/auth.model';
import { AuthService } from '../../../auth/auth.service';
import { takeUntilDestroy } from '../../../common/take-until-destroy.decorator';
import { useDispatcher, useStore } from '../../../store.decorator';
import {
    HubMethods,
    SocketService,
    TaskCreateNotification,
    TaskItem,
    TaskListNotification,
} from '../../core';

import { receiveTasks } from './state/receive-items.action';
import { reset } from './state/reset.action';
import { TaskListDialogComponent } from './task-list-dialog.component';
import { TranslationKeyMap } from './task-list-dialog.i18n';
import { taskListDialogReducer } from './task-list-dialog.reducer';
import { TaskListDialogState } from './task-list-dialog.state';

@Injectable({ providedIn: 'root' })
export class TaskListDialogService implements OnDestroy {
    state: TaskListDialogState;

    private readonly _didCloseSubj: Subject<void>;
    readonly didClose: Observable<void>;

    private _ref: CmpsDialogRef<TaskListDialogComponent, any>;
    private _refSub: Subscription;

    get isOpen(): boolean {
        return this._ref != null && !this._ref.closed;
    }

    constructor(
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
        private readonly _snackBar: CmpsSnackBar,
        readonly socketService: SocketService,
    ) {
        this._bindAuth();

        this._bind();

        this._didCloseSubj = new Subject<void>();
        this.didClose = this._didCloseSubj.asObservable();

        useStore(this, 'teamsCall.taskList', taskListDialogReducer);
    }

    ngOnDestroy(): void {
        this.close();
    }

    open(): void {
        if (this.isOpen) {
            return;
        }
        this._refSub?.unsubscribe();
        this._ref = this._dialog.open(TaskListDialogComponent, {
            disableOverlayOverflowHidden: true,
            overlayClass: 'cmps-task-list-dialog--overlay-class',
            portalClass: 'cmps-task-list-dialog--portal-class',
            portalWrapperClass: 'cmps-task-list-dialog--portal-wrapper-class',
            disableBackdrop: true,
            canClose: () => {
                /* TODO fix uikit auto-close for mouseup bubbling to document/window when nested in overlays */
                const filterHdl = document.querySelector(
                    '.cmps-task-list-search-filter--filter',
                );
                const ret2 =
                    filterHdl?.contains(mouseUpEvt.target as HTMLElement) !==
                    true;
                const sortMenuHdl = document.querySelector(
                    '.cmps-task-list-search-filter--sort-menu',
                )?.parentElement?.parentElement; // .cmps-menu
                const ret3 =
                    sortMenuHdl?.contains(mouseUpEvt.target as HTMLElement) !==
                    true;
                return ret2 && ret3;
            },
            placement: 'top-left',
        });
        const mousePos = { x: null, y: null };
        let mouseUpEvt: MouseEvent = null;
        this._refSub = merge(
            this._ref.result.pipe(
                tap(() => {
                    this._ref = null;
                    this._didCloseSubj.next();
                }),
            ),
            fromEvent<MouseEvent>(window, 'mousedown').pipe(
                tap((evt) => {
                    mousePos.x = evt.clientX;
                    mousePos.y = evt.clientY;
                }),
            ),
            fromEvent<MouseEvent>(window, 'mouseup').pipe(
                tap((evt) => {
                    mouseUpEvt = evt;
                }),
            ),
        )
            .pipe(takeUntilDestroy(this))
            .subscribe();
    }

    close(): void {
        // must call ref.close() before refSub.unsubscribe() so that ref.result() completes
        this._ref?.close();
        this._refSub?.unsubscribe();
    }

    toggle(): void {
        !this.isOpen ? this.open() : this.close();
    }

    createTask(item: TaskItem): void {
        if (this.socketService.connected) {
            void this.socketService.createTask(item);
        } else {
            this.socketService.didConnect
                .pipe(
                    tap((_socketService) => {
                        void this.socketService.createTask(item);
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        }
    }

    private _bindAuth(): void {
        this._authService.authStatus
            .pipe(
                tap((authStatus) => {
                    if (
                        [
                            AuthState.LOGOUT,
                            AuthState.TIMEOUT,
                            AuthState.LOST,
                        ].includes(this._authService.authState) &&
                        authStatus !== AuthStatus.AUTHENTICATED
                    ) {
                        this.close();
                        useDispatcher(this, reset({}));
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        this.socketService.message
            .pipe(
                tap((msg) => {
                    switch (msg.hubMethod) {
                        case HubMethods.OnTaskCreate:
                            this._onTaskCreate(msg.payload);
                            break;
                        case HubMethods.OnTaskList:
                            this._onTaskList(msg.payload);
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _onTaskCreate(payload: TaskCreateNotification | null): void {
        if (payload.task == null) {
            return;
        }

        useDispatcher(
            this,
            receiveTasks({ tasks: [payload.task], append: true }),
        );

        const data: CmpsTextOnlySnackBarOptions = {
            text: this._translateService.instant(
                TranslationKeyMap._11233_NEW_TASK_CREATED,
            ),
        };

        this._snackBar.open(CmpsTextOnlySnackBarComponent, {
            duration: 5000,
            placement: 'bottom-right',
            data,
        });
    }

    private _onTaskList(payload: TaskListNotification | null): void {
        useDispatcher(
            this,
            receiveTasks({ tasks: payload.tasks, append: false }),
        );
    }
}
